/**
 * Shapes returned by the members and invitations endpoints
 * (`competition_insights.modules.auth.schemas`). As everywhere in this app,
 * nothing here names a tenant: the server reads it from the session.
 */

import type { RoleRef } from '@/lib/api'

export type { RoleRef }

/** Mirrors `modules.auth.models.InviteStatus`. */
export type InviteStatus = 'pending' | 'accepted' | 'revoked' | 'expired'

/** Mirrors `MemberOut`. */
export interface Member {
  user_id: string
  email: string
  display_name: string | null
  role: RoleRef
  joined_at: string
  last_sign_in_at: string | null
}

/** Mirrors `InviteOut`. */
export interface Invite {
  id: string
  email: string
  role: RoleRef
  status: InviteStatus
  expires_at: string
  created_at: string
  last_sent_at: string | null
  send_count: number
}

export interface InviteInput {
  email: string
  role: string
}

/**
 * The roles this screen offers.
 *
 * `developer_support` is TmT's own support role and is never handed out from a
 * tenant screen (blueprint 03 §3.6) — it is absent from this list rather than
 * hidden, so there is no path through the UI that can select it. `owner` is on
 * the list but is a transfer of ownership, so the page offers it only to a
 * caller who holds `ownership.transfer`; the API refuses it either way.
 */
export const ASSIGNABLE_ROLES = [
  'owner',
  'administrator',
  'marketing_manager',
  'marketing_user',
  'analyst',
  'read_only',
] as const

export type AssignableRole = (typeof ASSIGNABLE_ROLES)[number]

/** Roles a caller without `ownership.transfer` may hand out. */
export function assignableRoles(canTransferOwnership: boolean): readonly AssignableRole[] {
  return canTransferOwnership ? ASSIGNABLE_ROLES : ASSIGNABLE_ROLES.filter((role) => role !== 'owner')
}
